import React, {useState, useEffect} from "react";

// Redux
import {useSelector, useDispatch} from "react-redux";
import {
  fetchEventVenue,
  editEventVenue,
  deleteEventVenue,
} from "../../../redux/features/events/venuesSlice";

// Routing
import {useParams, useHistory, Redirect} from "react-router-dom";

// Components
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Paper from "../../UI/MainPaper";
import EventVenuesForm from "./EventVenuesForm";
import FullContainerProgress from "../../UI/FullContainerProgress";
import DeleteButton from "../../Buttons/DeleteButton";
import DeleteDialog from "../../UI/DeleteDialog";
import BreadcrumbsWithEdit from "../../Navigation/BreadcrumbsWithEdit";

const EventVenuesEdit = () => {
  // Local State
  const [isEditMode, setEditMode] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Routing
  const {id} = useParams();
  const history = useHistory();

  // Redux
  const dispatch = useDispatch();
  const {currentPageEventVenue, isLoading, error} = useSelector(
    state => state.eventVenues
  );
  const {userPermission} = useSelector(state => {
    return state.auth;
  });
  const {write, delete: deletePermission} = userPermission.events;

  const formattedValues = {
    name: currentPageEventVenue.name,
    email: currentPageEventVenue.email,
    phone: currentPageEventVenue.phone,
    background: currentPageEventVenue.background,
    website: currentPageEventVenue.website,
    street: currentPageEventVenue.street,
    city: currentPageEventVenue.city,
    state: currentPageEventVenue.state,
    zip: currentPageEventVenue.zip,
    ticketmaster_id: currentPageEventVenue.ticketmaster_id,
  };

  let breadcrumbNameMap = {
    "/events": "Events",
    "/events/venues": "Event Venues",
  };
  breadcrumbNameMap[`/events/venues/${id}`] = currentPageEventVenue.name;

  useEffect(() => {
    // check if the id is a int to keep going
    // This if is why javascript is broken :)
    if (Number.isInteger(id * 1) && id * 1 < Number.MAX_SAFE_INTEGER) {
      dispatch(fetchEventVenue(id));
    } else {
      history.push("/404");
    }
  }, [dispatch, history, id]);

  useEffect(() => {
    setEditMode(write);
  }, [write]);

  const onSubmit = async values => {
    const eventVenueObj = {
      venue_id: id,
      ...values,
    };
    // Logic to show error or success
    const res = await dispatch(editEventVenue(eventVenueObj));
    if (!res.error) {
      history.push({
        pathname: "/events/venues",
        state: {
          showSuccess: true,
          snackbarContent: `Your changes to ${eventVenueObj.name} were saved successfully!`,
          snackbarLink: `/events/venues/${eventVenueObj.venue_id}`,
        },
      });
    }
  };

  const onDelete = async () => {
    // Logic to show error or success
    const res = await dispatch(deleteEventVenue(id));
    if (!res.error) {
      history.push({
        pathname: "/events/venues",
        state: {
          showSuccess: true,
          snackbarContent: `The Event Venue was successfully deleted!`,
        },
      });
    }
  };

  // Redirect if venue doesn't exist'
  if (error && error.errorCode === 404) {
    return <Redirect to={"/404"} />;
  }

  return (
    <>
      <Grid container spacing={2}>
        <BreadcrumbsWithEdit
          isLoading={isLoading}
          breadcrumbNameMap={breadcrumbNameMap}
          isEditMode={isEditMode}
          setEditMode={setEditMode}
          writePermission={write}
        />
        <Grid item xs={12}>
          <Paper>
            {isLoading ? (
              <FullContainerProgress />
            ) : (
              <>
                <Typography component="h1" variant="h5" gutterBottom>
                  {currentPageEventVenue.name}
                </Typography>
                <EventVenuesForm
                  defaultValues={formattedValues}
                  readOnly={!isEditMode}
                  submitButtonLabel="Save Changes"
                  onSubmit={onSubmit}
                  onCancel={() => setEditMode(false)}
                />
              </>
            )}
          </Paper>
        </Grid>
        {isEditMode && !isLoading && !!deletePermission && (
          <Grid item xs={12}>
            <DeleteButton onClick={() => setDialogOpen(true)} size="large">
              Delete Event Venue
            </DeleteButton>
          </Grid>
        )}
      </Grid>

      {!!deletePermission && (
        <DeleteDialog
          dialogTitle="Are you sure you want to delete this Event Venue?"
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={() => onDelete()}
        />
      )}
    </>
  );
};

export default EventVenuesEdit;
